import { AxiosResponse } from 'axios';
import { CookieManager } from "@Utilities/CookieManager";
import { TextResource } from "@Utilities/TextResource";
import { Validation } from "@Validation/Validation";
import { EngagementProvider } from "./Engagement";
import { Tenant } from "@Utilities/Tenant";
import queryString from "query-string";
import { DataPrivacyPolicy } from "@Utilities/DataPrivacyPolicy";
import Modal from "bootstrap/js/dist/modal";
import { ModalTools } from "@Utilities/ModalTools";
import { SubscriptionService } from '@Api/SubscriptionService';

export interface IEmailAcquisitionModal
{
	subscriptionId: string,
	prc: string,
	txtName: string,
	hasOffer: boolean,
	logo: {
		alt: string,
		src: string,
		width: number,
		height: number;
	};
}

export class SubscriptionBase
{
	private readonly dataPrivacyPolicy: DataPrivacyPolicy;
	private readonly engagementProvider: EngagementProvider;

	constructor()
	{
		this.dataPrivacyPolicy = new DataPrivacyPolicy();
		this.engagementProvider = new EngagementProvider();
	}

	private MaskPhoneInput = (form: HTMLFormElement): void =>
	{
		if (form.getAttribute("data-show-phone") !== null && form.getAttribute("data-show-phone") === "true")
		{
			const phoneCheck: HTMLInputElement = form.querySelector("#chkPhone");
			const phoneCollapse: HTMLElement = form.querySelector("#collapsePhone");
			const phoneInput = document.getElementById("txtPhone");
			let maskFormat = "000-000-0000";

			if (Tenant.DomainProjectID == Tenant.ID.HalloweenCostumesAU)
			{
				maskFormat = "0000 000 000";
			}

			if (phoneInput !== null)
			{
				phoneInput.addEventListener("focusin", () =>
				{
					import("imask")
						.then((IMask) =>
						{
							const phoneMask = new IMask.InputMask(phoneInput, {
								mask: maskFormat
							});
						});
				}, { once: true });

				if (phoneCheck !== null && phoneCollapse !== null)
				{
					phoneCheck.addEventListener("change", () =>
					{
						if (phoneCollapse.style.display === "none")
						{
							phoneCollapse.style.display = "block";
						}
						else
						{
							phoneCollapse.style.display = "none";
						}
					});
				}
			}
		}
	};

	public get GenericText(): string
	{
		return "";
	}

	public get GenericThanks(): string
	{
		return "";
	}

	public GetEmailAcquisitionModalHtml(config: IEmailAcquisitionModal): string
	{
		return "";
	}

	public ConfigureForm = (form: HTMLFormElement): void =>
	{
		if (form !== null)
		{
			this.MaskPhoneInput(form);
			this.CheckExclusions(form);

			if (!Tenant.ThirdPartyModalEnabled)
			{
				this.EvaluateEmailAcquisitionModal(form);
			}
		}
	};

	public CheckExclusions = (form: HTMLFormElement): void =>
	{
		const returnVisitorCookie = CookieManager.Cookies.ReturnVisitor;
		const hasOrdered = (CookieManager.GetCookie(returnVisitorCookie.Key, returnVisitorCookie.Ordered) === "true");
		const genericSubscriptionId = form.getAttribute("data-sub-generic");

		if (hasOrdered === true && typeof genericSubscriptionId !== "undefined" && genericSubscriptionId !== "")
		{
			this.MakeFormGeneric(form, genericSubscriptionId);
		}
	};

	public EvaluateEmailAcquisitionModal = (frmSubscribe: HTMLFormElement): void =>
	{
		const url = window.location.href.split('?')[0];
		const index = url.indexOf("/account/reviews/");
		let forceModal = false;

		if (location.search !== "")
		{
			const parsed = queryString.parse(location.search, { parseBooleans: true });
			forceModal = parsed.EmailModal as boolean;
		}

		if (index > -1)
		{
			// Prevent modal on review pages because they usually come from an email and don't want to interfere with that.  Per Joe B. 10/2018
			return;
		}

		if ((forceModal === true || CookieManager.GetCookie(CookieManager.Cookies.SeenModal.Key) !== "true") && this.dataPrivacyPolicy.DisplayModal === false)
		{
			const delayModal = CookieManager.GetCookie(CookieManager.Cookies.DelayModal.Key);

			//Check for modal delay cookie
			if (delayModal !== "")
			{
				const pages = parseInt(delayModal);

				if (!isNaN(pages) && pages > 0)
				{
					CookieManager.SetCookie("delayModal", (pages - 1).toString());
				}
				else
				{
					CookieManager.SetCookie("delayModal", "");
					this.RenderEmailAcquisitionModal("true");
				}
			}
			else
			{
				this.RenderEmailAcquisitionModal("true");
			}
		}
	};

	// update this html for attentive
	/**
	 * SHows our self hosted email acquisiton modal.
	 * @param force
	 * @param subscriptionId
	 * @param prc
	 * @param txtName
	 */
	public RenderEmailAcquisitionModal = (force?: string, subscriptionId?: string, prc?: string, txtName?: string): void =>
	{
		const frmSubscribe = document.querySelector("#frmSubscribe");
		const parsed = queryString.parse(location.search);
		const forceQueryString = parsed.EmailModal as string;

		if (frmSubscribe !== null)
		{
			//@ts-expect-error noModal MIGHT exist
			if (force || forceQueryString || (typeof noModal === "undefined" && CookieManager.GetCookie(CookieManager.Cookies.SeenModal) !== "true" && frmSubscribe.classList.contains("emailAcq")))
			{
				const returnVisitorCookie = CookieManager.Cookies.ReturnVisitor;
				const hasOrdered = CookieManager.GetCookie(returnVisitorCookie.Key, returnVisitorCookie.Ordered) == "true";
				let hasOffer = true;

				if (typeof subscriptionId === "undefined")
				{
					// no subscriptionId...fetch it from the footer, first the generic one
					let frmSubscribeAttribute;

					if (hasOrdered)
					{
						frmSubscribeAttribute = frmSubscribe.getAttribute("data-sub-generic");
					}

					// if the user hasn't ordered or if the generic offer above isn't defined use the data-sub attribute
					if (!hasOrdered || typeof frmSubscribeAttribute === "undefined")
					{
						frmSubscribeAttribute = frmSubscribe.getAttribute("data-sub");
					}

					// swap footer with popup
					subscriptionId = frmSubscribeAttribute.replace("Footer", "Popup");
				}

				if (typeof prc === "undefined")
				{
					prc = frmSubscribe.getAttribute("data-prc");
				}

				if (isNaN(parseInt(prc)) || parseInt(prc) === 0 || hasOrdered)
				{
					hasOffer = false;
				}

				if (typeof txtName === "undefined")
				{
					txtName = document.querySelector("#txtSubscribe").getAttribute("name");
				}

				const logoEl = document.querySelector(".navbar-brand img") as HTMLImageElement;

				const logo = {
					alt: logoEl.alt,
					src: logoEl.src,
					width: logoEl.width,
					height: logoEl.height
				};

				const modalWrap = document.createElement("div");
				modalWrap.innerHTML = this.GetEmailAcquisitionModalHtml({ subscriptionId: subscriptionId, prc: prc, txtName: txtName, hasOffer: hasOffer, logo: logo });
				const modalNode = modalWrap.firstElementChild;
				document.body.appendChild(modalNode);
				const emailAcqModal = new Modal(modalNode);
				emailAcqModal.show();

				modalNode.addEventListener("hidden.bs.modal", (event) =>
				{
					ModalTools.ModalDisposer(event.currentTarget);
				});

				CookieManager.SetCookie(CookieManager.Cookies.SeenModal.Key, "true", 5);
			}
		}
	};

	public MakeFormGeneric = (frmSubscribe: HTMLFormElement, genericSubscriptionId: string): void =>
	{
		const subscribeOffer = frmSubscribe.querySelector(".subscribe__offer");
		const subscribeText = frmSubscribe.querySelector(".subscribe__text");
		const subscribeThanks = frmSubscribe.querySelector(".subscribe__thx");
		const subscribeSent = frmSubscribe.querySelector(".subscribe__sent");

		frmSubscribe.setAttribute("data-sub", genericSubscriptionId);
		subscribeOffer.classList.add("subscribe__offer--generic");
		subscribeThanks.classList.add("subscribe__thx--generic");
		subscribeText.innerHTML = this.GenericText;
		subscribeSent.innerHTML = this.GenericThanks;
	};

	public Submit = (form: HTMLFormElement, showBanner = true, event?: Event): boolean =>
	{
		if (typeof event !== "undefined")
		{
			event.preventDefault();
		}

		// Reset phone input to empty string if the signup checkbox isn't selected
		const phoneCheck: HTMLInputElement = form.querySelector("#chkPhone");
		const phoneInput: HTMLInputElement = form.querySelector("input[type=tel]");

		if (phoneCheck !== null && phoneInput !== null && !phoneCheck.checked)
		{
			phoneInput.value = "";
		}

		const emailInput: HTMLInputElement = form.querySelector("input[type=email]");
		const email = emailInput !== null ? emailInput.value : "";
		const isEmailValid = Validation.Email(email);
		const phone = phoneInput !== null ? phoneInput.value : "";
		const subscriptionId = form.getAttribute("data-sub");
		let errors = "";

		if (isEmailValid === false)
		{
			errors += TextResource.GetTextByCulture("Feature_Shipping_ValidEmail");
		}

		const alert = form.querySelector(".alert");

		if (errors.length > 0)
		{
			if (alert === null)
			{
				const alertNode = document.createElement("div");
				alertNode.innerHTML = `<div class="alert alert-danger" role="alert">${errors}</div>`;

				form.prepend(alertNode.firstChild);
			}
			else
			{
				alert.innerHTML = errors;
			}

			return false;
		}
		else
		{
			if (alert !== null)
			{
				alert.remove();
			}
		}

		this.engagementProvider.AddSubscription(phone, email, subscriptionId);
		this.engagementProvider.CreateGuestAccount(email, phone, isEmailValid, phone !== "");

		const offer: HTMLElement = form.querySelector(".subscribe__offer");
		const thanks: HTMLElement = form.querySelector(".subscribe__thx");
		const address: HTMLElement = form.querySelector(".subscribe__address");

		offer.style.display = "none";
		thanks.style.display = "block";
		address.textContent = email;

		CookieManager.SetCookie("modalSignedUp", "true");

		return false;
	};

	public UpdateFooter = (): void =>
	{
		const subscriptionService = new SubscriptionService();
		subscriptionService.GetFooter()
			.then((response: AxiosResponse) =>
			{
				const footerHtml = response.data;

				if (footerHtml !== "")
				{
					const footer = document.querySelector("#footer-subscribe");

					if (footer !== null)
					{
						footer.outerHTML = footerHtml;
						const frmSubscribe = document.querySelector("#frmSubscribe") as HTMLFormElement;

						if (frmSubscribe !== null)
						{
							const showPhone = frmSubscribe.getAttribute("data-show-phone");

							if (showPhone !== null && showPhone === "true")
							{
								this.MaskPhoneInput(frmSubscribe);
							}
						}
					}
				}
			})
			.catch((error) =>
			{
				console.log(error);
			});
	};
}